import {EventProcessStageTitle, Tag} from '@hconnect/common/components'
import {getAssigneesValue} from '@hconnect/common/utils'
import {DateFormat} from '@hconnect/uikit'
import {Grid} from '@mui/material'
import {getEquipmentLabelDetails, EventStatusLabel} from '@widgets/common-shift-event'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {MachineSentryActionDto} from '../../types'

import {TaskDescription} from './TaskDescription'

type Props = {
  machineSentryAction: MachineSentryActionDto
  plantId: string
  plantName: string
  timezone: string
}

export const MachineSentryActionDetails = ({
  machineSentryAction,
  plantId,
  plantName,
  timezone
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const translate = (key: string) => t(key)
  const noneOfThatLabel = translate('task.noneOfThat')

  const assigneesValue: string = useMemo(() => {
    const assignees = getAssigneesValue(machineSentryAction.assignees, t)
    return assignees ? assignees : noneOfThatLabel
  }, [noneOfThatLabel, machineSentryAction, t])

  const functionalLocation =
    machineSentryAction.machineSentryProperties?.functionalLocation ?? noneOfThatLabel
  const machineSentryProcessStage =
    machineSentryAction.machineSentryProperties?.processStage ?? noneOfThatLabel

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tag label={translate('task.label.status')} data-test-id="task-details-status">
            <EventStatusLabel status={machineSentryAction.status} />
          </Tag>
        </Grid>
        <Grid item md={12}>
          <Tag label={translate('task.label.priority')} data-test-id="task-details-priority">
            {machineSentryAction.priority
              ? translate(`machineSentryActions.priority.${machineSentryAction.priority}`)
              : noneOfThatLabel}
          </Tag>
        </Grid>

        <Grid item md={12}>
          <Tag label={translate('task.label.dueDate')} data-test-id="task-details-dueDate">
            {machineSentryAction.dueDate ? (
              <DateFormat date={machineSentryAction.dueDate} timezone={timezone} />
            ) : (
              noneOfThatLabel
            )}
          </Tag>
        </Grid>
        <Grid item md={12}>
          <Tag label={translate('task.label.plant')} data-test-id="task-details-plant">
            {plantName}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            data-test-id="task-details-mainEquipment"
            label={translate('task.label.mainEquipment')}
          >
            {getEquipmentLabelDetails(machineSentryAction.mainEquipment, false)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag data-test-id="task-details-equipment" label={translate('task.label.equipment')}>
            {getEquipmentLabelDetails(machineSentryAction.equipment)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            label={translate('machineSentryActions.label.machineSentryProcessStage')}
            data-test-id="task-details-machineSentryProcessStage"
          >
            {machineSentryProcessStage}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            label={translate('machineSentryActions.label.processStage')}
            data-test-id="task-details-processStage"
          >
            {machineSentryAction.processStage ? (
              <EventProcessStageTitle plantId={plantId} stage={machineSentryAction.processStage} />
            ) : (
              noneOfThatLabel
            )}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            label={translate('machineSentryActions.label.machineSentryFunctionalLocation')}
            data-test-id="task-details-machineSentryFunctionalLocation"
          >
            {functionalLocation}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag data-test-id="task-details-assignees" label={translate('task.label.assignee')}>
            {assigneesValue}
          </Tag>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tag
            label={translate('task.label.description')}
            descriptionSx={{fontWeight: 400, fontSize: 14}}
            data-test-id="task-details-description"
          >
            <TaskDescription description={machineSentryAction.description} />
          </Tag>
        </Grid>
      </Grid>
    </>
  )
}
